/**
 * PROTOTYPE rollup: fold per-spread engine results into spread totals and the
 * project grand total. No pricing is computed here; each spread's numbers come
 * from the engine (see spreadPricingApi). The only project-level addition is the
 * welding services line, billed once for the whole project.
 */
import { ProjectState, SpreadPricing, ModuleLine } from './spreadSplitData';
import type { ModulePriceResult, SpreadPriceResult } from './spreadPricingApi';
import type { SpreadPriceState } from './useSpreadPrice';

/** Sum of a spread's six module totals from the engine. Disabled modules price to 0. */
export function spreadResultTotal(r: SpreadPriceResult): number {
  return r.welding.total + r.mt.total + r.ndt.total + r.coating.total + r.platform.total + r.retention.total;
}

/** A spread's real total once the engine has returned, else null (still pending). */
export function spreadTotal(state: SpreadPriceState | undefined): number | null {
  return state?.status === 'done' ? spreadResultTotal(state.result) : null;
}

/** Project grand total: every priced spread plus the once-only welding services (from
 *  the engine, passed in). A spread still loading or errored contributes 0 until it
 *  resolves; servicesTotal defaults to 0 (services disabled or not yet priced). */
export function realProjectTotal(project: ProjectState, statesById: Record<string, SpreadPriceState>, servicesTotal = 0): number {
  const spreadSum = project.spreads.reduce((sum, s) => sum + (spreadTotal(statesById[s.id]) ?? 0), 0);
  return spreadSum + servicesTotal;
}

/** How many spreads have not yet returned a real price (loading, error, or missing). */
export function pricingPending(project: ProjectState, statesById: Record<string, SpreadPriceState>): number {
  return project.spreads.filter((s) => statesById[s.id]?.status !== 'done').length;
}

const zeroLine: ModuleLine = { price: 0, cost: 0, margin: 0 };
/** A not-yet-priced spread renders as zeros, never the placeholder rates. */
export const ZERO_PRICING: SpreadPricing = {
  welding: zeroLine, mt: zeroLine, ndt: zeroLine, coating: zeroLine, platform: zeroLine, retention: zeroLine, total: 0,
};

function moduleLine(m: ModulePriceResult): ModuleLine {
  return { price: m.total, cost: m.cost, margin: m.total > 0 ? 1 - m.cost / m.total : 0 };
}

/** Convert a real engine result into the SpreadPricing shape the workspace and the
 *  documents render (price = engine total, cost = engine cost, margin derived). */
export function pricingFromResult(r: SpreadPriceResult): SpreadPricing {
  const welding = moduleLine(r.welding), mt = moduleLine(r.mt), ndt = moduleLine(r.ndt);
  const coating = moduleLine(r.coating), platform = moduleLine(r.platform), retention = moduleLine(r.retention);
  const total = welding.price + mt.price + ndt.price + coating.price + platform.price + retention.price;
  return { welding, mt, ndt, coating, platform, retention, total };
}

/** A spread's real pricing once the engine returns, else zeros (never the placeholder). */
export function spreadPricing(state: SpreadPriceState | undefined): SpreadPricing {
  return state?.status === 'done' ? pricingFromResult(state.result) : ZERO_PRICING;
}

/** Real pricing for every spread, keyed by id, for the documents to render. */
export function spreadPricingMap(project: ProjectState, statesById: Record<string, SpreadPriceState>): Record<string, SpreadPricing> {
  return Object.fromEntries(project.spreads.map((s) => [s.id, spreadPricing(statesById[s.id])]));
}
